import { useNavigate, useParams } from 'react-router-dom'

import {
  Box,
  Button,
  Icon,
  Text,
  Tooltip,
  useToggle as useToggleHook,
} from '@factorialco/deprecated-design-system'
import { isString, omitBy } from 'lodash'

import { Authorized } from 'modules/core/components/Authorized'
import Modal from 'modules/core/components/Modal'
import Selector from 'modules/core/components/Selector'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useEffect } from 'modules/core/hooks/useEffect'
import { useForm } from 'modules/core/hooks/useForm'
import type { FormErrors } from 'modules/core/hooks/useForm'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'

import { useCreateAtsCvBulkUpload } from 'modules/ats/components/UploadCvsModal/useCreate'
import { useCandidateSources } from 'modules/ats/hooks/candidateSources/useCandidateSources'
import { useApplicationPhases } from 'modules/ats/hooks/useApplicationPhases'
import { getSourceLabel } from 'modules/ats/lib/utils/candidateSources'
import {
  CV_BULK_UPLOAD_ACCEPTED_CONTENT_TYPES,
  CV_BULK_UPLOAD_LIMIT,
  UploadCvsFormValues,
} from 'modules/ats/lib/utils/cvBulkUpload'
import DropzoneBox from 'modules/documents/components/Documents/DropzoneBox'
import FileList from 'modules/documents/components/FileList'

const JobApplicationCreateFromCvPage = () => {
  const { jobId } = useParams()
  const navigate = useNavigate()
  const { navigation } = useNavigation()
  const [showSourceTooltip, toggleSourceTooltip] = useToggleHook(false)
  const { loading: loadingCandidateSources, candidateSources } = useCandidateSources()
  const { create } = useCreateAtsCvBulkUpload()
  const {
    access,
    company: { id: companyId, subscription },
  } = useCurrentSession()
  const atsJobPostingId = String(jobId)
  const { applicationPhases: jobPhases, loading: loadingJobPhases } = useApplicationPhases({
    variables: { atsJobPostingId },
  })

  const navigateToJobApplication = () =>
    navigate(
      navigation.ats.jobs.jobsId.applications.fullPath({
        jobId: atsJobPostingId,
      })
    )

  const form = useForm<UploadCvsFormValues>({
    defaultValues: {
      accessId: access.id,
      atsCandidateSourceId: '',
      companyId: companyId,
      files: [],
    },
    validate: (data) => {
      const errors: FormErrors<UploadCvsFormValues> = {}

      if (!data.atsCandidateSourceId) {
        errors.atsCandidateSourceId = i18n.t('common.validations.empty')
      }

      if (!data.files.length) {
        errors.files = i18n.t('common.validations.at_least_one_file')
      }

      if (data.files.length > CV_BULK_UPLOAD_LIMIT) {
        errors.files = i18n.t('common.validations.max_files', { count: CV_BULK_UPLOAD_LIMIT })
      }

      if (atsJobPostingId && !data.atsApplicationPhaseId) {
        errors.atsApplicationPhaseId = i18n.t('common.validations.empty')
      }

      return errors
    },
    onSubmit: async (data, { setErrors }) => {
      const result = await create({
        atsJobPostingId,
        atsCandidateSourceId: data.atsCandidateSourceId,
        atsApplicationPhaseId: data.atsApplicationPhaseId,
        files: data.files,
        accessId: data.accessId,
        companyId: data.companyId,
      })

      const handled = handleResult(result, (errors) => {
        setErrors(errors)
      })

      if (handled.success) {
        track(trackingEvents.ats.cvBulkUploadApplicantsRequested, {
          company_id: companyId,
          files_count: data.files.length,
        })

        navigateToJobApplication()
      }

      return handled
    },
  })

  useEffect(() => {
    form.setFieldValue(
      'atsCandidateSourceId',
      candidateSources.find(({ category }) => category === 'manually_added')?.id
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadingCandidateSources])

  useEffect(() => {
    const phaseId = jobPhases.find((phase) => phase.name.toLocaleLowerCase() === 'new')?.id

    phaseId && form.setFieldValue('atsApplicationPhaseId', phaseId)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.values.atsJobPostingId, atsJobPostingId, loadingJobPhases])

  useEffect(() => {
    track(trackingEvents.ats.cvBulkUploadApplicantsInitiated, {
      company_id: companyId,
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const onPhaseChange = (newId: unknown) => {
    form.setFieldTouched('atsApplicationPhaseId')
    form.setFieldValue('atsApplicationPhaseId', newId)
  }

  const onDrop = (files: File[]) => {
    if (form.values.files.length + files.length > CV_BULK_UPLOAD_LIMIT) {
      form.setFieldError(
        'files',
        i18n.t('common.validations.max_files', { count: CV_BULK_UPLOAD_LIMIT })
      )
      return
    }

    form.setFieldTouched('files')
    form.setFieldValue('files', form.values.files.concat(files))
  }

  const onRemoveFile = (indexToRemove: number) => {
    form.setFieldValue(
      'files',
      form.values.files.filter((_file, index) => index !== indexToRemove)
    )
  }

  const dropzoneDisabled = form.values.files.length >= CV_BULK_UPLOAD_LIMIT
  const availableSources = candidateSources.filter(({ category }) => category !== 'organic')

  const errors = omitBy(form.errors, (_value, key) => !Object.keys(form.touched).includes(key))

  return (
    <Modal
      illustration="document-edit"
      title={i18n.t('ats.candidates.upload_resumes.modal.title')}
      description={i18n.t('ats.candidates.upload_resumes.modal.description')}
      onClose={navigateToJobApplication}
      onSubmit={() => form.handleSubmit()}
      footer={
        <>
          <Button onClick={navigateToJobApplication} type="secondary">
            {i18n.t('common.cancel')}
          </Button>
          <Button submit disabled={form.isSubmitting}>
            {i18n.t('ats.candidates.upload_resumes.modal.submit')}
          </Button>
        </>
      }
    >
      <Box gap="s24">
        <Box gap="s24">
          <Selector.Popover
            label={i18n.t('ats.candidates.upload_resumes.modal.source_select_label')}
            searchPlaceholder={i18n.t('common.search')}
            placeholder={i18n.t('ats.common.select_source')}
            blankLabel={i18n.t('common.empty')}
            value={[form.values.atsCandidateSourceId]}
            onChange={(value) => form.setFieldValue('atsCandidateSourceId', value)}
            mandatory
            error={isString(errors.atsCandidateSourceId) ? errors.atsCandidateSourceId : undefined}
            isDisabled={loadingCandidateSources}
            secondaryLabel={
              <Tooltip.Alt
                open={showSourceTooltip}
                toggle={toggleSourceTooltip}
                label={i18n.t('ats.candidates.upload_resumes.modal.source_select_tooltip')}
                attachment="middle left"
              >
                <Icon.Small icon="question" encircled="grey800" />
              </Tooltip.Alt>
            }
          >
            {availableSources.map((option) => (
              <Selector.Option
                key={option.id}
                value={option.id}
                label={getSourceLabel(option.name)}
                searchValue={option.name}
              >
                {getSourceLabel(option.name)}
              </Selector.Option>
            ))}
          </Selector.Popover>

          <Selector.Popover
            label={i18n.t('ats.candidates.upload_resumes.modal.phase_select_label')}
            searchPlaceholder={i18n.t('common.search')}
            placeholder={i18n.t('ats.common.select_phase')}
            blankLabel={i18n.t('common.empty')}
            value={[form.values.atsApplicationPhaseId]}
            onChange={onPhaseChange}
            error={
              isString(errors.atsApplicationPhaseId) ? errors.atsApplicationPhaseId : undefined
            }
            loading={loadingJobPhases}
            isDisabled={jobPhases.length < 1}
          >
            {jobPhases.map((phase) => (
              <Selector.Option
                key={`phase-${phase.id}`}
                value={phase.id}
                label={phase.name}
                searchValue={phase.name}
              >
                {phase.name}
              </Selector.Option>
            ))}
          </Selector.Popover>
        </Box>
        <Authorized subscription={subscription}>
          <Box gap="s4">
            <DropzoneBox
              onDrop={onDrop}
              message={i18n.t('documents.drop_here')}
              accept={CV_BULK_UPLOAD_ACCEPTED_CONTENT_TYPES()}
              disabled={dropzoneDisabled}
            />
            {isString(errors.files) && (
              <Text.Normal color="radicalRed1000" capitalize size="200">
                {errors.files}
              </Text.Normal>
            )}
          </Box>
        </Authorized>

        {!!form.values.files?.length && (
          <FileList spaced={false} files={form.values.files} onDeleteFile={onRemoveFile} />
        )}
      </Box>
    </Modal>
  )
}

export default JobApplicationCreateFromCvPage
